import { computed, ref } from 'vue'
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'
import { useThrottleFn } from '@vueuse/core'
import { apolloClient, type DocumentNode } from '@/vendor/apollo-client'
import { catalogQuery } from '@/data/catalogClient.ts'

export function usePagination<T extends DocumentNode, D>({
  query,
  takeNumber,
  getPaginationData,
  maxItemsToLoad,
  initialVariables = {},
}: {
  query: T
  takeNumber: number
  getPaginationData: (result: ResultOf<T>) => D[]
  maxItemsToLoad?: number
  initialVariables?: Partial<VariablesOf<T>>
}) {
  const items = ref<D[]>([])
  const firstLoadDone = ref(false)
  const endOfList = ref(false)
  const noItems = ref(false)
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  let currentQuery: T = query
  let currentGetPaginationData: (result: ResultOf<T>) => D[] = getPaginationData

  function getQueryRootFieldNames(doc: DocumentNode): string[] {
    const fields: string[] = []
    for (const def of doc.definitions) {
      if (def.kind === 'OperationDefinition' && def.selectionSet) {
        for (const sel of def.selectionSet.selections) {
          if (sel.kind === 'Field') {
            fields.push(sel.name.value)
          }
        }
      }
    }
    return fields
  }

  function evictQueryCache() {
    const fieldNames = getQueryRootFieldNames(currentQuery)
    for (const fieldName of fieldNames) {
      apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName })
    }
    apolloClient.cache.gc()
  }

  const take = ref(maxItemsToLoad && maxItemsToLoad < takeNumber ? maxItemsToLoad : takeNumber)

  const fetchParams = ref({
    variables: {
      ...initialVariables,
      take,
      skip: 0,
    } as VariablesOf<T> & { take: number; skip: number },
  })

  const loadNextPageImmediate = async () => {
    if (endOfList.value || isLoading.value) {
      return
    }
    fetchParams.value = {
      ...fetchParams.value,
      variables: {
        ...fetchParams.value.variables,
        take: maxItemsToLoad && maxItemsToLoad - items.value.length <= 0 ? 0 : take,
        skip: fetchParams.value.variables.skip + take.value,
      },
    }
    await fetchData(false)
  }

  const loadNextPage = useThrottleFn(loadNextPageImmediate, 1000, true)

  const loadUntilSkip = async (targetSkip: number) => {
    while (fetchParams.value.variables.skip < targetSkip && !endOfList.value && !isLoading.value) {
      await loadNextPageImmediate()
    }
  }

  const resetPagination = async (variables: Partial<VariablesOf<T>>) => {
    fetchParams.value = {
      ...fetchParams.value,
      variables: {
        ...fetchParams.value.variables,
        ...variables,
        skip: 0,
        take: take,
      },
    }
    endOfList.value = false
    noItems.value = false
    evictQueryCache()
    await fetchData(true)
  }

  const fetchData = async (reset: boolean = false) => {
    try {
      isLoading.value = true
      const responseData = await catalogQuery({
        query: currentQuery,
        variables: fetchParams.value.variables as VariablesOf<T>,
        fetchPolicy: reset ? 'network-only' : 'cache-first',
      })

      firstLoadDone.value = true
      const extracted = currentGetPaginationData(responseData.data as ResultOf<T>)

      if (reset) {
        items.value = [...extracted]
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        items.value.push(...(extracted as any[]))
      }

      if (maxItemsToLoad && items.value.length > maxItemsToLoad) {
        items.value = items.value.slice(0, maxItemsToLoad)
        endOfList.value = true
      }

      if (!endOfList.value) {
        endOfList.value = extracted.length === 0
      }
      noItems.value = items.value.length === 0
    } catch (e) {
      if (e instanceof Error) {
        error.value = e
      }
    }
    isLoading.value = false
  }

  const updateConfig = (newQuery: T, newGetPaginationData: (result: ResultOf<T>) => D[]) => {
    currentQuery = newQuery
    currentGetPaginationData = newGetPaginationData
  }

  const setFetchParams = (variables: Partial<VariablesOf<T>>) => {
    fetchParams.value = {
      ...fetchParams.value,
      variables: {
        ...fetchParams.value.variables,
        ...variables,
      },
    }
  }

  fetchData()

  const currentSkip = computed(() => fetchParams.value.variables.skip)

  return {
    loadNextPage,
    loadNextPageImmediate,
    loadUntilSkip,
    currentSkip,
    firstLoadDone,
    isLoading,
    items,
    noItems,
    endOfList,
    error,
    resetPagination,
    setFetchParams,
    updateConfig,
  }
}
